import React, { useEffect, useRef, useState, useCallback } from "react";
import {
  FaStore,
  FaUserTie,
  FaBuilding,
  FaQrcode,
  FaExchangeAlt,
  FaMoneyBillWave,
  FaCheckCircle,
} from "react-icons/fa";
import { Chart, registerables } from "chart.js";
import {
  getDashboardSummary,
  getDashboardTrends,
  getDashboardQrAdoption,
  getDashboardRecentActivities,
  getDashboardTopOrganizations,
} from "../api/api";
import "../styles/Dashboard.css";

Chart.register(...registerables);

/* ─── Static label sets ─────────────────────────────────────── */
const LABELS = {
  week:  ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"],
  month: ["Week 1","Week 2","Week 3","Week 4"],
  year:  ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"],
};

/* ─── Helpers ────────────────────────────────────────────────── */
const sum  = (arr) => arr.reduce((a, b) => a + b, 0);
const peak = (arr) => Math.max(...arr);
const fmt  = (n)   => n >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(n);

const num = (v) => { const n = Number(v); return Number.isFinite(n) ? n : 0; };

/* Pull an array out of whatever envelope the API returns */
function extractArray(res, ...keys) {
  const payload = res?.data ?? res;
  if (Array.isArray(payload)) return payload;
  for (const k of keys) {
    if (Array.isArray(payload?.[k])) return payload[k];
    if (Array.isArray(res?.[k])) return res[k];
  }
  return [];
}

function buildData(apiData, filter) {
  const arr = Array.isArray(apiData) ? apiData : [];
  return LABELS[filter].map((label) => {
    const match = arr.find?.(
      (d) => String(d.label ?? d.period ?? d.name) === label
    );
    return {
      label,
      count: num(match?.count ?? match?.transactions ?? match?.value),
      amount: num(match?.amount ?? match?.revenue ?? match?.volume),
    };
  });
}

/* ── Summary tiles ── */
function mapSummary(res) {
  const s = res?.data ?? res ?? {};
  return {
    totalOrganizations: num(s.totalOrganizations ?? s.totalOrgs ?? s.organizations),
    activeOrganizations: num(s.activeOrganizations ?? s.activeOrgs),
    merchants: num(s.activeMerchants ?? s.totalMerchants ?? s.merchants),
    transactions: num(s.totalTransactions ?? s.transactions ?? s.transactionCount),
    revenue: num(s.totalRevenue ?? s.revenue ?? s.totalAmount ?? s.totalVolume),
    dynamicQrShare: num(s.dynamicQrPercentage ?? s.dynamicQrShare ?? s.dynamicQr),
    staticQrShare: num(s.staticQrPercentage ?? s.staticQrShare ?? s.staticQr),
    successRate: num(s.successRate ?? s.success),
  };
}

/* ── QR adoption doughnut (data-driven from API list) ── */
const QR_COLORS = ["#534AB7", "#1D9E75", "#64748b", "#f59e0b"];
function mapQrAdoption(res) {
  return extractArray(res, "data", "methods", "adoption").map((x, i) => ({
    label: x.name ?? x.label ?? x.type ?? `Method ${i + 1}`,
    value: num(x.value ?? x.count),
    percentage: x.percentage != null ? num(x.percentage) : null,
    color: QR_COLORS[i % QR_COLORS.length],
  }));
}

/* ── Relative time for activities ── */
function timeAgo(value) {
  if (!value) return "";
  const then = new Date(value).getTime();
  if (Number.isNaN(then)) return String(value);
  const diff = Math.max(0, Date.now() - then);
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min${mins > 1 ? "s" : ""} ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hour${hrs > 1 ? "s" : ""} ago`;
  const days = Math.floor(hrs / 24);
  return `${days} day${days > 1 ? "s" : ""} ago`;
}

/* Detect raw URLs / API paths so we never dump them into the UI */
const looksLikeUrl = (s) =>
  typeof s === "string" && /^(https?:\/\/|\/)|\/api\//.test(s.trim());

/* "/system/dashboard/summary" -> "Dashboard Summary" */
function humanizePath(path) {
  if (typeof path !== "string") return "";
  const clean = path.split("?")[0].replace(/^\/?(api\/)?/, "").replace(/\/\d+/g, "");
  const parts = clean.split("/").filter(Boolean);
  return parts
    .slice(-2)
    .join(" ")
    .replace(/[-_]/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();
}

function mapActivities(res) {
  return extractArray(res, "activities", "data", "logs").map((a) => {
    const method = a.method || a.httpMethod;
    const url = a.url || a.path || a.endpoint || a.route;
    const who = a.username || a.user || a.actor || a.performedBy || "System";

    /* Prefer a human-readable field; never show a raw URL */
    let title = [a.title, a.event, a.activity, a.description, a.action].find(
      (v) => v && !looksLikeUrl(v)
    );
    if (!title) title = url ? humanizePath(url) : "Activity";

    const desc =
      [a.message, a.details, a.desc].find((v) => v && !looksLikeUrl(v)) ||
      [method ? `${method} request` : null, who && who !== "System" ? `by ${who}` : null]
        .filter(Boolean)
        .join(" ");

    return {
      title,
      desc,
      when: a.when || a.time || timeAgo(a.timestamp || a.createdAt || a.date),
    };
  });
}

function mapTopOrganizations(res) {
  return extractArray(res, "organizations", "data", "orgs").map((o) => {
    const volumeRaw = o.dailyVolume ?? o.volume ?? o.volume30Days ?? o.totalVolume;
    const volume =
      typeof volumeRaw === "string"
        ? volumeRaw
        : `Rs ${num(volumeRaw).toLocaleString()}`;
    const successRaw = o.successRate ?? o.success ?? 0;
    return {
      name: o.name ?? o.orgName ?? "—",
      volume,
      success: typeof successRaw === "string" ? successRaw : `${num(successRaw)}%`,
      status: String(o.status ?? "ACTIVE").toUpperCase(),
    };
  });
}

function Kpi({ label, value, icon, accent, trend, progress }) {
  return (
    <div className="msd-kpi">
      <div className="msd-kpi__head">
        <div className="msd-kpi__label">{label}</div>
        <div className="msd-kpi__icon" style={{ color: accent, background: accent + "22" }}>
          {icon}
        </div>
      </div>
      <div className="msd-kpi__value">{value}</div>

      {typeof trend === "string" && trend.length > 0 ? (
        <div className="msd-kpi__trend">{trend}</div>
      ) : null}

      {progress ? (
        <div className="msd-kpi__progress">
          <div className="msd-kpi__progress-bar" style={{ width: `${Math.max(0, Math.min(100, progress))}%`, background: accent }} />
        </div>
      ) : (
        <div className="msd-kpi__spark" style={{ background: `linear-gradient(90deg, ${accent} 0%, transparent 80%)` }} />
      )}
    </div>
  );
}

/* ─── Chart factory helpers ──────────────────────────────────── */
const PURPLE  = "#534AB7";
const TEAL    = "#1D9E75";
const GRID    = "rgba(0,0,0,0.06)";
const TICK    = "#9ca3af";

const tooltipDefaults = {
  backgroundColor: "#1e1b4b",
  titleColor: "#fff",
  bodyColor: "#c4b5fd",
  padding: 10,
  cornerRadius: 8,
  displayColors: false,
};

function lineConfig(labels, values, color, prefix = "") {
  return {
    type: "line",
    data: {
      labels,
      datasets: [{
        data: values,
        borderColor: color,
        borderWidth: 2.5,
        pointBackgroundColor: color,
        pointBorderColor: "#fff",
        pointBorderWidth: 1.5,
        pointRadius: 4,
        pointHoverRadius: 6,
        fill: true,
        backgroundColor: (ctx) => {
          const g = ctx.chart.ctx.createLinearGradient(0, 0, 0, 180);
          g.addColorStop(0, color + "40");
          g.addColorStop(1, color + "00");
          return g;
        },
        tension: 0.4,
      }],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      animation: { duration: 550, easing: "easeInOutQuart" },
      plugins: {
        legend: { display: false },
        tooltip: {
          ...tooltipDefaults,
          callbacks: {
            label: (ctx) => `  ${prefix}${Number(ctx.parsed.y).toLocaleString()}`,
          },
        },
      },
      scales: {
        x: { grid: { display: false }, ticks: { color: TICK, font: { size: 11 } } },
        y: {
          grid: { color: GRID, drawBorder: false },
          ticks: {
            color: TICK, font: { size: 11 },
            callback: (v) => (v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v),
          },
        },
      },
    },
  };
}

function barConfig(labels, values) {
  const maxVal = Math.max(...values);
  const colors = values.map((v) =>
    v === maxVal ? PURPLE : v > maxVal * 0.65 ? "#7F77DD" : "#AFA9EC"
  );
  return {
    type: "bar",
    data: {
      labels,
      datasets: [{
        data: values,
        backgroundColor: colors,
        borderRadius: 6,
        borderSkipped: false,
        barPercentage: 0.6,
      }],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      animation: { duration: 550, easing: "easeInOutQuart" },
      plugins: {
        legend: { display: false },
        tooltip: {
          ...tooltipDefaults,
          callbacks: { label: (ctx) => `  Count: ${ctx.parsed.y.toLocaleString()}` },
        },
      },
      scales: {
        x: { grid: { display: false }, ticks: { color: TICK, font: { size: 11 } } },
        y: {
          grid: { color: GRID, drawBorder: false },
          ticks: {
            color: TICK, font: { size: 11 },
            callback: (v) => (v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v),
          },
        },
      },
    },
  };
}

function doughnutConfig(active, inactive) {
  return {
    type: "doughnut",
    data: {
      labels: ["Active", "Inactive"],
      datasets: [{
        data: [active, inactive],
        backgroundColor: [PURPLE, "#EEEDFE"],
        borderColor: [PURPLE, "#AFA9EC"],
        borderWidth: 1.5,
        hoverOffset: 8,
      }],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      cutout: "68%",
      animation: { duration: 700, animateRotate: true },
      plugins: {
        legend: { display: false },
        tooltip: { ...tooltipDefaults },
      },
    },
  };
}

/* Data-driven QR adoption doughnut (from qr-adoption API list) */
function qrDoughnut(items) {
  const total = items.reduce((s, i) => s + i.value, 0);

  /* When there's no data yet (all zeros / empty), show a full grey ring
     so the doughnut is still visible instead of rendering blank */
  const isEmpty = !items.length || total === 0;
  const list = isEmpty
    ? (items.length ? items : [{ label: "Dynamic QR", color: "#534AB7" }, { label: "Static QR", color: "#1D9E75" }])
    : items;

  return {
    type: "doughnut",
    data: {
      labels: list.map((i) => i.label),
      datasets: [{
        data: isEmpty ? list.map(() => 1) : list.map((i) => i.value),
        backgroundColor: isEmpty ? list.map(() => "#EEEDFE") : list.map((i) => i.color),
        borderColor: list.map(() => "#fff"),
        borderWidth: 1.5,
        hoverOffset: 8,
      }],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      cutout: "68%",
      animation: { duration: 700, animateRotate: true },
      plugins: {
        legend: { display: false },
        tooltip: {
          ...tooltipDefaults,
          callbacks: {
            /* Always show the real value/percentage, never the placeholder 1 */
            label: (ctx) => {
              const item = list[ctx.dataIndex] || {};
              const value = isEmpty ? 0 : Number(item.value ?? 0);
              const percentage =
                item.percentage != null
                  ? item.percentage
                  : total
                    ? Math.round((value / total) * 100)
                    : 0;
              return `  ${value.toLocaleString()} (${percentage}%)`;
            },
          },
        },
      },
    },
  };
}

/* ─── Reusable chart canvas wrapper ──────────────────────────── */
function ChartCanvas({ id, config, height = 200 }) {
  const ref = useRef(null);
  const instanceRef = useRef(null);

  useEffect(() => {
    if (!ref.current || !config) return;
    if (instanceRef.current) instanceRef.current.destroy();
    instanceRef.current = new Chart(ref.current, config);
    return () => { if (instanceRef.current) instanceRef.current.destroy(); };
  }, [config]);

  return (
    <div style={{ position: "relative", width: "100%", height }}>
      <canvas ref={ref} id={id} />
    </div>
  );
}

/* ─── Stat mini-tile ─────────────────────────────────────────── */
function MiniStat({ label, value }) {
  return (
    <div style={{
      flex: 1, background: "#f9fafb", borderRadius: 10,
      padding: "10px 14px", border: "0.5px solid #e5e7eb",
    }}>
      <p style={{ margin: 0, fontSize: 11, color: "#9ca3af", textTransform: "uppercase", letterSpacing: ".04em" }}>{label}</p>
      <p style={{ margin: "3px 0 0", fontSize: 20, fontWeight: 700, color: "#1e1b4b" }}>{value}</p>
    </div>
  );
}

/* ─── Legend pill ────────────────────────────────────────────── */
function Legend({ items }) {
  return (
    <div style={{ display: "flex", gap: 16, marginTop: 10 }}>
      {items.map((item) => (
        <span key={item.label} style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 12, color: "#6b7280" }}>
          <span style={{
            width: 10, height: 10, borderRadius: 2,
            background: item.color,
            border: item.border ? `0.5px solid ${item.border}` : undefined,
          }} />
          {item.label}
        </span>
      ))}
    </div>
  );
}

/* ─── Top stat tile ──────────────────────────────────────────── */
function Tile({ label, value, icon, accent }) {
  return (
    <div style={{
      position: "relative", background: "#fff", borderRadius: 16,
      padding: "1.2rem 1.3rem 1rem", overflow: "hidden", cursor: "pointer",
      boxShadow: "0 2px 8px rgba(0,0,0,0.06), 0 0 0 1px rgba(0,0,0,0.04)",
      transition: "transform .22s, box-shadow .22s",
    }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = "translateY(-3px)";
        e.currentTarget.style.boxShadow = "0 10px 24px rgba(0,0,0,0.12), 0 0 0 1px rgba(0,0,0,0.05)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = "";
        e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.06), 0 0 0 1px rgba(0,0,0,0.04)";
      }}
    >
      {/* left accent bar */}
      <div style={{
        position: "absolute", left: 0, top: 0, bottom: 0, width: 4,
        background: accent, borderRadius: "16px 0 0 16px",
      }} />
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
        <div style={{
          width: 36, height: 36, borderRadius: 10,
          background: accent + "22", color: accent,
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: "1.15rem",
        }}>
          {icon}
        </div>
        <p style={{ margin: 0, fontSize: "0.78rem", fontWeight: 600, color: "#9ca3af", textTransform: "uppercase", letterSpacing: ".05em" }}>
          {label}
        </p>
      </div>
      <p style={{ margin: 0, fontSize: "2rem", fontWeight: 700, color: "#1e1b4b", lineHeight: 1 }}>
        {Number(value).toLocaleString()}
      </p>
    </div>
  );
}

/* ─── Chart card wrapper ─────────────────────────────────────── */
function ChartCard({ title, children, action }) {
  return (
    <div style={{
      background: "#fff", borderRadius: 16, padding: "1.4rem 1.4rem 1.2rem",
      boxShadow: "0 2px 8px rgba(0,0,0,0.06), 0 0 0 1px rgba(0,0,0,0.04)",
      transition: "box-shadow .2s",
    }}
      onMouseEnter={(e) => { e.currentTarget.style.boxShadow = "0 6px 20px rgba(0,0,0,0.10)"; }}
      onMouseLeave={(e) => { e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.06), 0 0 0 1px rgba(0,0,0,0.04)"; }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.75rem" }}>
        <h3 style={{ margin: 0, fontSize: "1rem", fontWeight: 600, color: "#1e1b4b" }}>{title}</h3>
        {action}
      </div>
      {children}
    </div>
  );
}

/* ─── Main dashboard ─────────────────────────────────────────── */
const AdminDashboard = ({ isSidebarOpen }) => {
  const [summary, setSummary]     = useState(null);
  const [loading, setLoading]     = useState(true);
  const [filter, setFilter]       = useState("week");
  const [chartData, setChartData] = useState([]);
  const [chartLoading, setChartLoading] = useState(false);
  const [qr, setQr]               = useState([]);
  const [activities, setActivities] = useState([]);
  const [topOrganizations, setTopOrganizations] = useState([]);

  /* fetch summary + qr + activities + top organizations on mount */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [summaryRes, qrRes, activityRes, topOrgRes] = await Promise.all([
          getDashboardSummary().catch(() => null),
          getDashboardQrAdoption().catch(() => null),
          getDashboardRecentActivities(10).catch(() => null),
          getDashboardTopOrganizations(5).catch(() => null),
        ]);
        if (cancelled) return;
        setSummary(mapSummary(summaryRes));
        setQr(mapQrAdoption(qrRes));
        setActivities(mapActivities(activityRes));
        setTopOrganizations(mapTopOrganizations(topOrgRes));
      } catch (err) {
        console.error("Error loading dashboard:", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  /* fetch chart data whenever filter changes */
  const fetchCharts = useCallback(async (f) => {
    setChartLoading(true);
    try {
      const res = await getDashboardTrends(f);
      setChartData(buildData(extractArray(res, "trends", "data"), f));
    } catch {
      setChartData(LABELS[f].map((label) => ({ label, count: 0, amount: 0 })));
    } finally {
      setChartLoading(false);
    }
  }, []);

  useEffect(() => { fetchCharts(filter); }, [filter, fetchCharts]);

  /* derived stats */
  const counts  = chartData.map((d) => d.count);
  const amounts = chartData.map((d) => d.amount);
  const labels  = chartData.map((d) => d.label);

  const chartCount  = sum(counts);
  const chartAmount = sum(amounts);

  /* prefer summary totals, fall back to chart-derived totals */
  const totalCount  = summary?.transactions || chartCount;
  const totalAmount = summary?.revenue || chartAmount;

  const dynamicQrShare = summary?.dynamicQrShare ?? 0;
  const staticQrShare = summary?.staticQrShare ?? 0;
  const successRate = summary?.successRate ?? 0;

  /* chart configs (memoised on data changes) */
  const countLineConfig  = chartData.length ? lineConfig(labels, counts, PURPLE) : null;
  const amountLineConfig = chartData.length ? lineConfig(labels, amounts, TEAL, "Rs. ") : null;
  const barChartConfig   = chartData.length ? barConfig(labels, counts) : null;

  /* QR adoption doughnut driven by real qr-adoption data */
  const qrTotal = qr.reduce((s, x) => s + x.value, 0);
  const qrPct = (x) =>
    x.percentage != null ? x.percentage : qrTotal ? Math.round((x.value / qrTotal) * 100) : 0;
  const doughnutConf = qrDoughnut(qr);

  /* filter pill */
  const FilterBar = () => (
    <div style={{ display: "flex", gap: 6 }}>
      {["week","month","year"].map((f) => (
        <button
          key={f}
          onClick={() => setFilter(f)}
          style={{
            padding: "5px 14px", borderRadius: 20, fontSize: 12, cursor: "pointer",
            border: filter === f ? "none" : "0.5px solid #e5e7eb",
            background: filter === f ? PURPLE : "#f9fafb",
            color: filter === f ? "#fff" : "#6b7280",
            fontWeight: filter === f ? 600 : 400,
            transition: "all .18s",
          }}
        >
          {f === "week" ? "This week" : f === "month" ? "This month" : "This year"}
        </button>
      ))}
    </div>
  );

  const tiles = [
    {
      label: "Total Orgs",
      value: Number(summary?.totalOrganizations ?? 0).toLocaleString(),
      icon: <FaBuilding />,
      accent: "#534AB7",
      trend: "0%",
    },
    {
      label: "Merchants",
      value: fmt(summary?.merchants ?? 0),
      icon: <FaStore />,
      accent: "#1D9E75",
      trend: "0%",
    },
    {
      label: "Transactions",
      value: fmt(totalCount),
      icon: <FaExchangeAlt />,
      accent: "#1976D2",
      trend: "0%",
    },
    {
      label: "Revenue",
      value: totalAmount ? `Rs. ${fmt(totalAmount)}` : "Rs. 0",
      icon: <FaMoneyBillWave />,
      accent: "#D85A30",
      trend: "⚡",
    },
    {
      label: "Dynamic QR",
      value: `${dynamicQrShare}%`,
      icon: <FaQrcode />,
      accent: "#22c55e",
      progress: dynamicQrShare,
    },
    {
      label: "Static QR",
      value: `${staticQrShare}%`,
      icon: <FaQrcode />,
      accent: "#f59e0b",
      progress: staticQrShare,
    },
    {
      label: "Success Rate",
      value: `${successRate}%`,
      icon: <FaCheckCircle />,
      accent: "#06b6d4",
      progress: Math.max(0, Math.min(100, Number(successRate))),
    },
  ];

  /* ── Render ─────────────────────────────────────────────────── */
  if (loading) {
    return (
      <div className={`msd-container ${isSidebarOpen ? "shifted" : ""}`}>
        <p className="msd-muted">Loading dashboard…</p>
      </div>
    );
  }

  return (
    <div className={`msd-container ${isSidebarOpen ? "shifted" : ""}`}>
      <div className="msd-kpis">
        {tiles.map((t, i) => (
          <Kpi
            key={i}
            label={t.label}
            value={t.value}
            icon={t.icon}
            accent={t.accent}
            trend={t.trend}
            progress={t.progress}
          />
        ))}
      </div>

      <div className="msd-maingrid">
        <div className="msd-card msd-card--wide">
          <div className="msd-card__head">
            <div>
              <div className="msd-card__title">Transaction Trends</div>
              <div className="msd-card__subtitle">Volume and value analysis across orgs</div>
            </div>
            <div className="msd-card__actions">
              <FilterBar />
            </div>
          </div>

          <div className="msd-card__body">
            {chartLoading
              ? <p className="msd-muted">Loading…</p>
              : <ChartCanvas id="msd-trends" config={countLineConfig} height={240} />
            }
          </div>
        </div>

        <div className="msd-card">
          <div className="msd-card__head">
            <div>
              <div className="msd-card__title">QR Adoption</div>
              <div className="msd-card__subtitle">Method distribution</div>
            </div>
          </div>
          <div className="msd-card__body msd-donut">
            <div className="msd-donut__chart">
              <ChartCanvas id="msd-qr" config={doughnutConf} height={180} />
            </div>
            <div className="msd-donut__legend">
              {qr.length ? (
                qr.map((item) => (
                  <div key={item.label} className="msd-legend-row">
                    <span className="msd-dot" style={{ background: item.color }} />
                    {item.label}
                    <span className="msd-legend-val">{qrPct(item)}%</span>
                  </div>
                ))
              ) : (
                <div className="msd-legend-row"><span className="msd-dot" style={{ background: "#EEEDFE" }} />No QR data<span className="msd-legend-val">0%</span></div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="msd-bottomgrid">
        <div className="msd-card">
          <div className="msd-card__head">
            <div>
              <div className="msd-card__title">Recent Activities</div>
              <div className="msd-card__subtitle">System events and onboarding updates</div>
            </div>
          </div>
          <div className="msd-activity">
            {activities.length ? (
              activities.map((a, i) => (
                <div key={i} className="msd-activity__row">
                  <div className="msd-activity__meta">
                    <div className="msd-activity__title">{a.title}</div>
                    <div className="msd-activity__desc">{a.desc}</div>
                  </div>
                  <div className="msd-activity__when">{a.when}</div>
                </div>
              ))
            ) : (
              <p className="msd-muted">No recent activities</p>
            )}
          </div>
        </div>

        <div className="msd-card msd-card--wide">
          <div className="msd-card__head">
            <div>
              <div className="msd-card__title">Top Organizations</div>
              <div className="msd-card__subtitle">Daily volume and success rate</div>
            </div>
          </div>
          <div className="msd-table">
            <div className="msd-table__head">
              <div>Organization</div>
              <div>Daily Volume</div>
              <div>Success Rate</div>
              <div>Status</div>
            </div>
            {topOrganizations.length ? (
              topOrganizations.map((o, i) => (
                <div key={i} className="msd-table__row">
                  <div className="msd-org">
                    <div className="msd-org__avatar">{String(o.name).slice(0, 1)}</div>
                    <div className="msd-org__name">{o.name}</div>
                  </div>
                  <div className="msd-mono">{o.volume}</div>
                  <div className="msd-mono">{o.success}</div>
                  <div>
                    <span className={o.status === "ACTIVE" ? "msd-badge msd-badge--active" : "msd-badge msd-badge--review"}>
                      {o.status}
                    </span>
                  </div>
                </div>
              ))
            ) : (
              <p className="msd-muted" style={{ padding: "0.75rem" }}>No organizations yet</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
